import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { debounce } from "lodash";
import { PlusCircle, XCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../ui/command";
import { Separator } from "../../../ui/separator";
import { Spinner } from "../../../ui/spinner";
import Checkbox from "../../inputs/Checkbox";
import Button from "../../Button";
import useApi from "../../../../utils/api";
import { renderSettingsResourceLabel } from "../../../../utils/helpers/settings";

function Filter({ label, url, renderItem, getSearchFilters, value, filterKey, method }: any) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(value || []);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState<string | undefined>();
    const [, setSearchParams] = useSearchParams();
    const { authGet } = useApi();
    const queryClient = useQueryClient();

    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false,
    });

    const debouncedSetSearch = useMemo(() => {
        return debounce((value: string) => {
            setSearch(value);
            queryClient.removeQueries({ queryKey: [url] });
        }, 500);
    }, [queryClient, url]);

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [debouncedSetSearch]);

    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } = useInfiniteQuery({
        queryKey: [url, { search }],
        queryFn: async ({ queryKey, pageParam }: any) => {
            const [endpoint, params] = queryKey;
            const filters = getSearchFilters
                ? getSearchFilters(params?.search)
                : params?.search
                    ? { search: params?.search }
                    : {};
            const response = await authGet(endpoint, {
                params: {
                    ...filters,
                    page: pageParam,
                    perPage: 20,
                },
            });
            return response;
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const loadedItems = allPages.reduce((acc, page) => acc + (page?.result?.length || 0), 0);
            if (loadedItems < (lastPage?.totalItems || 0)) {
                return allPages.length + 1;
            }
            return undefined;
        },
    });

    useEffect(() => {
        if (!inView) return;
        if (!hasNextPage || isFetchingNextPage) return;

        fetchNextPage();
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const _data = useMemo(() => {
        if (!data?.pages) return [];
        const allItems = data.pages.flatMap((page: any) => page?.result || []);

        return allItems.sort((a: any, b: any) => {
            const aSelected = selected.some((item: any) => item.id === a.id);
            const bSelected = selected.some((item: any) => item.id === b.id);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return 0;
        });
    }, [data?.pages, selected]);

    const applySelectionToUrl = useCallback((newSelected: any[]) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);

            for (const name of Array.from(next.keys())) {
                const match = name.match(/^filter\[([^\]]+)\]/);
                if (match?.[1] === filterKey) {
                    next.delete(name);
                }
            }

            if (newSelected.length > 0) {
                const filterName = `filter[${filterKey}][${method || "exact"}]`;

                newSelected.forEach((item) => {
                    next.append(`${filterName}[]`, item.id);
                });
            }

            next.set("pageIndex", "0");

            if (next.toString() === prev.toString()) {
                return prev;
            }

            return next;
        });
    }, [setSearchParams, filterKey, method]);

    const onSelect = useCallback((item: any) => {
        const exists = selected.some((value: any) => value.id === item.id);

        const newSelected = exists
            ? selected.filter((value: any) => value.id !== item.id)
            : [...selected, item];

        setSelected(newSelected);
        applySelectionToUrl(newSelected);
    }, [selected, applySelectionToUrl]);

    useEffect(() => {
        const nextValue = Array.isArray(value)
            ? value
            : value
                ? [value]
                : [];

        const isSame = selected.length === nextValue.length &&
            selected.every((selectedItem: any) =>
                nextValue.some(
                    (valueItem: any) =>
                        valueItem.id === selectedItem.id,
                ),
            );

        if (!isSame) {
            setSelected(nextValue);
        }
    }, [value]); // intentionally only reacts to URL/external value changes

    useEffect(() => {
        if (!value || !open) return;

        const fetchMissing = async () => {
            const selectedItems = Array.isArray(value) ? value : [value];
            const itemsToFetch = selectedItems.filter((item: any) => {
                const cached = queryClient.getQueryData([
                    "resource",
                    url,
                    "item",
                    item.id,
                ]);
                const existsInData = data?.pages?.some((page: any) =>
                    page?.result?.some(
                        (resource: any) =>
                            resource.id === item.id,
                    ),
                );
                return !cached && !existsInData;
            });

            if (!itemsToFetch.length) {
                return;
            }

            const responses = await Promise.all(
                itemsToFetch.map((item: any) =>
                    authGet(`${url}/${item.id}`),
                ),
            );

            itemsToFetch.forEach((item: any, index: number) => {
                queryClient.setQueryData(
                    ["resource", url, "item", item.id],
                    responses[index],
                );
            });

            queryClient.setQueryData(
                [url, { search }],
                (oldData: any) => {
                    if (!oldData?.pages?.length) {
                        return oldData;
                    }

                    const allItems = oldData.pages.flatMap(
                        (page: any) => page?.result || [],
                    );

                    const merged = [
                        ...allItems,
                        ...responses,
                    ].filter(
                        (item: any, index: number, array: any[]) =>
                            array.findIndex(
                                (existing: any) =>
                                    existing.id === item.id,
                            ) === index,
                    );

                    if (merged.length === allItems.length) {
                        return oldData;
                    }

                    const pageSize =
                        oldData.pages[0]?.result?.length ||
                        merged.length;

                    const pages = [];

                    for (
                        let i = 0;
                        i < merged.length;
                        i += pageSize
                    ) {
                        pages.push({
                            result: merged.slice(
                                i,
                                i + pageSize,
                            ),
                        });
                    }

                    return {
                        ...oldData,
                        pages,
                    };
                },
            );
        };

        fetchMissing();
    }, [value, url, queryClient, authGet, search, open, data?.pages]);

    const clearSelection = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        setSelected([]);
        applySelectionToUrl([]);
    }, [applySelectionToUrl]);

    return (
        <Popover
            key={url}
            open={open}
            onOpenChange={setOpen}
        >
            <PopoverTrigger
                id={url}
                asChild
                onClick={(event) => event.stopPropagation()}
            >
                <Button
                    variant="outline"
                    className="tw-border-dashed tw-shadow-none tw-h-10"
                    onClick={(event) =>
                        event.stopPropagation()
                    }
                >
                    {selected.length > 0 && !open ? (
                        <div
                            className="tw-cursor-pointer"
                            onClick={clearSelection}
                        >
                            <XCircle />
                        </div>
                    ) : (
                        <PlusCircle />
                    )}
                    {label}
                    {selected.length > 0 ? (
                        <span className="tw-flex tw-items-center tw-gap-1">
                            <Separator
                                orientation="vertical"
                                className="tw-mx-0.5 data-[orientation=vertical]:tw-h-4"
                            />
                            <span className="tw-rounded-sm tw-bg-neutral-100 tw-text-xs tw-px-2 tw-py-1">
                                {selected.length}{" "} {t("selected")}
                            </span>
                        </span>
                    ) : null}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="tw-p-1"
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={`${t("search")}...`}
                        onValueChange={debouncedSetSearch}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {isLoading ? (<Spinner />) : (t("no-result"))}
                        </CommandEmpty>
                        <CommandGroup>
                            {_data.map((item: any) => {
                                const checked = selected.some((value: any) =>
                                    value?.id === item.id,
                                );

                                const labelText = renderItem
                                    ? renderItem(item)
                                    : renderSettingsResourceLabel(item);

                                return (
                                    <CommandItem
                                        className="tw-cursor-pointer"
                                        key={item.id}
                                        value={labelText}
                                        onSelect={() => onSelect(item)}
                                    >
                                        <Checkbox
                                            checked={checked}
                                        />
                                        <span className="tw-truncate">
                                            {labelText}
                                        </span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {hasNextPage && !isLoading && (
                            <div
                                ref={ref}
                                className="tw-py-2 tw-text-center tw-text-sm tw-text-muted-foreground"
                            >
                                {isFetchingNextPage ? t("loading") : t("loading-more")}
                            </div>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default Filter;
